import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, FileText, Percent, Globe } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What should a Sri Lankan invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A Sri Lankan invoice should include: your business name and address; your TIN (Taxpayer Identification Number) if VAT registered; the client name and address; a unique invoice number; the invoice date; a clear description of goods or services; the amount in LKR; and your payment details. VAT-registered businesses must also show the VAT rate (18%), the VAT amount, and the total inclusive of VAT.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the VAT rate in Sri Lanka?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The standard VAT rate in Sri Lanka is 18% as of 2024, increased from 15%. Businesses must register for VAT with the Inland Revenue Department (IRD) once annual taxable supplies exceed LKR 80 million. Registered businesses must add 18% VAT to all standard-rated supplies and show the VAT amount separately on their invoices.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do Sri Lankan freelancers invoice international clients?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Sri Lankan freelancers typically invoice overseas clients in USD, GBP, or EUR. Services exported to foreign clients are generally zero-rated for VAT purposes (0% VAT), meaning you do not charge VAT on international invoices. Common payment methods include Payoneer, Wise, bank wire transfer (SWIFT), and Stripe. Sri Lankan banks accept foreign currency transfers to your local account.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is a TIN in Sri Lanka?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A TIN (Taxpayer Identification Number) is the unique tax ID issued by the Inland Revenue Department (IRD) of Sri Lanka. You need a TIN to register for VAT and to file income tax. Individuals and businesses can register for a TIN at any IRD district office or online through the IRD portal. Include your TIN on all tax invoices if you are VAT registered.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is this invoice generator free for Sri Lankan users?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic is completely free for Sri Lankan freelancers and businesses. Create professional invoices in LKR, USD, GBP, or any currency. Download a clean PDF instantly — no sign-up required for up to 5 invoices per month.',
      },
    },
  ],
};

const invoiceTypes = [
  {
    icon: Percent,
    title: 'VAT Invoice',
    subtitle: 'For VAT-registered Sri Lankan businesses',
    features: ['18% VAT auto-calculated', 'TIN / VAT number field', 'Net + VAT + total', 'IRD-compliant format', 'LKR currency'],
    to: '/create/vat',
    color: 'orange',
    btnClass: 'bg-orange-600 hover:bg-orange-700',
  },
  {
    icon: FileText,
    title: 'Standard Invoice',
    subtitle: 'For businesses below LKR 80M threshold',
    features: ['No VAT required', 'LKR currency', 'Custom line items', 'Logo upload', 'Payment terms'],
    to: '/create/freelancer',
    color: 'slate',
    btnClass: 'bg-slate-700 hover:bg-slate-800',
  },
  {
    icon: Globe,
    title: 'International Invoice',
    subtitle: 'For billing overseas clients',
    features: ['USD, GBP, EUR billing', 'Zero-rated export format', 'SWIFT / Payoneer details', 'Multi-currency support', 'Professional PDF'],
    to: '/create/international',
    color: 'indigo',
    btnClass: 'bg-indigo-600 hover:bg-indigo-700',
  },
];

export default function InvoiceGeneratorSriLankaPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Invoice Generator Sri Lanka: Free LKR Invoice Maker | 18% VAT | Invilic"
        description="Free invoice generator for Sri Lankan businesses and freelancers. Create VAT invoices with 18% VAT or standard LKR invoices. PDF download free, no sign-up required."
        canonical="https://www.invilic.com/invoice-generator-sri-lanka"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <span className="inline-block bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            🇱🇰 Sri Lanka
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Invoice Generator Sri Lanka
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Free invoice generator for Sri Lankan businesses, freelancers, and sole traders. Create VAT invoices with 18% VAT auto-calculated, or standard LKR invoices. Download professional PDF invoices instantly — no sign-up needed.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/create/vat" className="inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-orange-600/20 transition-all duration-200">
              Create VAT Invoice <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/create/freelancer" className="inline-flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-800 text-white font-semibold px-6 py-4 rounded-xl transition-all duration-200">
              Create Standard Invoice <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">Which Invoice Do You Need?</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {invoiceTypes.map(({ icon: Icon, title, subtitle, features, to, color, btnClass }) => (
              <div key={title} className={`border border-${color}-100 dark:border-${color}-800/30 rounded-2xl p-6 bg-${color}-50 dark:bg-${color}-900/10`}>
                <div className={`w-10 h-10 rounded-xl bg-${color}-100 dark:bg-${color}-900/40 flex items-center justify-center mb-4`}>
                  <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1">{title}</h3>
                <p className={`text-xs text-${color}-700 dark:text-${color}-300 mb-4`}>{subtitle}</p>
                <ul className="space-y-2 mb-5">
                  {features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <CheckCircle className={`w-3.5 h-3.5 text-${color}-500 flex-shrink-0`} /> {f}
                    </li>
                  ))}
                </ul>
                <Link to={to} className={`inline-flex items-center justify-center gap-1.5 w-full px-4 py-2.5 ${btnClass} text-white text-sm font-semibold rounded-xl transition-all`}>
                  Create Free <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16 p-8 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-5">Sri Lanka VAT Invoice vs Standard Invoice</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <div className="font-semibold text-orange-700 dark:text-orange-400 mb-3">Use a VAT Invoice if:</div>
              {[
                'Your annual taxable supplies exceed LKR 80 million',
                'You are VAT registered with the Inland Revenue Department (IRD)',
                'Your client needs a tax invoice showing the 18% VAT breakdown',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
            <div>
              <div className="font-semibold text-slate-700 dark:text-slate-400 mb-3">Use a Standard Invoice if:</div>
              {[
                'Your annual supplies are below the LKR 80 million VAT threshold',
                'You are a freelancer or sole trader not yet VAT registered',
                'You are billing an overseas client (exports are typically zero-rated)',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mb-16 p-6 rounded-2xl border border-orange-100 dark:border-orange-800/40 bg-orange-50 dark:bg-orange-900/20">
          <h2 className="font-bold text-gray-900 dark:text-white mb-3">Receiving International Payments as a Sri Lankan Freelancer</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Sri Lanka's freelance sector — especially in IT, design, and content — earns heavily in USD. Here are the best payment options:
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { label: 'Payoneer', detail: 'Widely used by Sri Lankan freelancers on Upwork and Fiverr. Withdraw to LKR via local bank or spend with Payoneer Mastercard.' },
              { label: 'Wise (formerly TransferWise)', detail: 'Lower fees than traditional SWIFT. Receive USD, GBP, or EUR and convert to LKR at mid-market rates.' },
              { label: 'Bank wire (SWIFT)', detail: 'Sri Lankan banks (Bank of Ceylon, Commercial Bank, Sampath) all accept SWIFT international transfers. Provide your bank name, SWIFT code, and account number.' },
              { label: 'Stripe', detail: 'Available in Sri Lanka since 2023. Collect card payments from international clients and withdraw to a local LKR bank account.' },
            ].map(({ label, detail }) => (
              <div key={label} className="p-3 bg-white dark:bg-gray-900/50 rounded-xl border border-orange-100 dark:border-orange-800/30">
                <p className="font-semibold text-gray-900 dark:text-white text-xs mb-1">{label}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{detail}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Sri Lanka Invoice Generator: Frequently Asked Questions</h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">{acceptedAnswer.text}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Create Your Sri Lankan Invoice for Free</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF for Sri Lankan businesses in under 2 minutes. No account required.</p>
          <Link to="/create/vat" className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-orange-600/20 transition-all duration-200 text-lg">
            Create Invoice Free <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
    </div>
  );
}
